import { ReportsNavigationModule } from "@app/main/components/reports/reports.navigation";
import { ResourceManagementNavigationModule } from "@app/main/components/resourceManagement/resourceManagement.navigation";
import { AlertsNavigationModule } from "@app/main/modules/alerts/alerts.navigation";
import { FleetNavigationModule } from "@app/main/modules/fleet/fleet.navigation";
import { FoundationNavigationModule } from "@app/main/modules/foundation/foundation.navigation";
import { BroadcastEvent } from "@enums/BroadcastEvent";
import { SpecialUserPermissions } from "@enums/SpecialUserPermissions";
import { AccountFeatures } from "@enums/account/AccountFeature";
import { IRunBlockRootScope } from "@interfaces/IRunBlockRootScope";
import { UserPlatform } from "@interfaces/IUser";
import { IVehicle } from "@interfaces/IVehicle";
import { IAccount } from "@services/account.service";
import { BreadcrumbService, IBreadcrumbNameResolver } from "@services/breadcrumb.service";
import { TemperatureNavigationModule } from "@temperature/temperature.navigation";
import { TmsModuleNavigation } from "@tms/tms.navigation";
import { RawParams, StateOrName, StateService } from "@uirouter/core";
import * as moment from "moment";
import { CameraModule } from "src/app/main/modules/cameras/camera.module.ajs";
import { PageStateNames } from "../class/PageStateNames";
import { ResourceType } from "../enum/ResourceType";
import { UserType } from "../enum/UserType";
import { AccountFeatureService, UserRole } from "./accountFeature.service";
import { ConstantsService } from "./constantsService";
import { VehicleService } from "./vehicle.service";

export class NavigationService {

    readonly defaultFeatureRouteMap: { [feature: number]: string } = {
        [AccountFeatures.Breathalock]: FleetNavigationModule.Breathalock,
        [AccountFeatures.Messaging]: FleetNavigationModule.Messages,
        [AccountFeatures.Tacho]: FleetNavigationModule.Tachograph,
        [AccountFeatures.TrackingDevice]: FleetNavigationModule.Vehicles,
        [AccountFeatures.Camera]: CameraModule.Default,
    }

    readonly defaultResourceManagementRouteMap: { [role: string]: string } = {
        [UserRole.ManageDrivers]: ResourceManagementNavigationModule.DriversManagement,
        [UserRole.ManageGroups]: ResourceManagementNavigationModule.GroupsManagement,
        [UserRole.ManageVehicles]: ResourceManagementNavigationModule.VehiclesManagement,
        [UserRole.Administrator]: ResourceManagementNavigationModule.Default
    }

    readonly mobileOnlyFeatureRoutes: string[] = [
        FleetNavigationModule.Vehicles,
        FleetNavigationModule.Temperature,
        FleetNavigationModule.Dashboards,
        TmsModuleNavigation.Default
    ];

    static $inject = ["$window", "$rootScope", "$state", "vehicleService", "breadcrumbService", "accountFeatureService", "constantsService", "$location"];

    constructor(
        private readonly $window: angular.IWindowService,
        private readonly $rootScope: IRunBlockRootScope,
        private readonly $state: StateService,
        private readonly vehicleService: VehicleService,
        private readonly breadcrumbService: BreadcrumbService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly constantsService: ConstantsService,
        private readonly $location: angular.ILocationService
    ) {
    }

    goToParent() {
        const parentStateName = this.$state.$current.parent.name;
        this.$state.go(parentStateName);
    }

    updateTitle(title: string) {
        this.$state.$current.data.displayName = title;
        this.$rootScope.$broadcast(BroadcastEvent.UpdateBreadcrumbs);
    }

    updateBreadcrumbNameResolver(nameResolver: IBreadcrumbNameResolver) {
        this.breadcrumbService.updateNameResolver(nameResolver);
        this.$rootScope.$broadcast(BroadcastEvent.UpdateBreadcrumbs);
    }

    redirectToFoundation() {
        if (this.$rootScope.currentUser?.Type == UserType.InternalUser) {
            this.$state.go(FoundationNavigationModule.Default, this.$rootScope.toStateParams);
        }

        if (this.$rootScope.currentUser?.Type == UserType.Engineer) {
            this.$state.go(FoundationNavigationModule.DefaultEngineer, this.$rootScope.toStateParams);
        }
    }

    reloadBrowserLocation(): void {
        this.$window.location.reload();
    }

    getLocation(): string {
        return this.$window.location.pathname;
    }

    reloadAll() {
        ///*   var targetRoute =*/ this.$.reload();
        //this.$state.go(this.$state.current.name, {}, { reload: true });
        //this.$state.go(this.$state.current, {}, { reload: true });
        //this.$state.go(PageStateNames.COMPONENTS_PLANNING, {}, { reload: true });
        this.goToDefaultPage(null, true);
        //AUTHENTICATION_LOCK
    }

    goToDefaultPage(accountData?: IAccount, reload = false) {
        var targetRoute = this.$rootScope.toState?.name;

        if (this.$state.current.data?.previous?.name && this.$state.current.data.previous.name !== PageStateNames.AUTHENTICATION_FORGOTPASSWORD) {
            targetRoute = this.$state.current.data.previous.name;
        }

        if (!targetRoute || !this.canUserAccessRoute(targetRoute)) {
            targetRoute = this.getDefaultRouteForUser(accountData);
        }

        if (!accountData && targetRoute == PageStateNames.FOUNDATION_CUSTOMERS_OVERVIEW && this.canUserAccessRoute(targetRoute)) {
            targetRoute = PageStateNames.FOUNDATION_CUSTOMERS
        }

        if (!this.$rootScope.currentUser) {
            targetRoute = PageStateNames.AUTHENTICATION_LOGIN;
        }

        this.$state.go(targetRoute, this.$rootScope.toStateParams, { reload: reload });
    }

    goToVehiclesPage(vehicle: IVehicle, historical = false, date: moment.Moment = null) {
        const sanitizedReg = this.vehicleService.getSanitizedReg(vehicle);

        const routeParams: RawParams = { "vehicle": sanitizedReg };
        if (!historical) {
            routeParams["panel"] = "live";
        }
        else {
            var isToday = date == null || date.isSame(moment(), "d");;

            routeParams["panel"] = "history";
            routeParams["date"] = isToday ? "today" : date.format("YYYY-MM-DD");
        }

        this.goToState(FleetNavigationModule.Vehicles, routeParams);
    }

    editVehicle(vehicle: IVehicle) {
        this.goToState(PageStateNames.COMPONENTS_RESOURCEMANAGEMENT, {
            page: "vehicles",
            model: vehicle
        });
    }

    goToMessagesPage(vehicle: IVehicle) {
        const routeParams: RawParams = { vehicle: vehicle.Id };

        this.goToState(FleetNavigationModule.Messages, routeParams);
    }

    go(pageName: PageStateNames, params: RawParams = null, reloadIfSame = true) {

        this.goToState(pageName, params, reloadIfSame);
    }

    goTo(pageName: PageStateNames, page: string, params: RawParams = null, reloadIfSame = true) {

        let routeParams: RawParams = {
            page: page,
        };

        routeParams = Object.assign(routeParams, params);

        this.goToState(pageName, routeParams, reloadIfSame);
    }

    goToStateString(stateString: string, params: RawParams = null, reloadIfSame = true) {
        let routeParams: RawParams = {};

        routeParams = Object.assign(routeParams, params);

        this.goToState(stateString, routeParams, reloadIfSame);
    }

    getRouteParam(paramName: string) {
        return this.$state.params[paramName];
    }

    getQueryParams<T = any>(): Partial<T> {
        return this.$location.search();
    }

    setRouteParam(paramName: string, value: any, reloadPage = true) {
        this.$state.params[paramName] = value;

        if (reloadPage) {
            this.$state.go(this.$state.current.name, this.$state.params, { location: "replace", notify: false, reload: false });
        }
    }

    clearRouteParam(paramName: string): void {
        delete this.$state.params[paramName];
    }

    goToResourcesPage(resourceType: ResourceType, modelId: number) {
        //app.components_resourceManagement({ page: 'geofences', model: $ctrl.popupModel.Id, modelType: 'geofence', panel: 'edit' })
        const typeString = ResourceType[resourceType].toLocaleLowerCase();
        this.goTo(PageStateNames.COMPONENTS_RESOURCEMANAGEMENT, `${typeString}s`, { model: modelId, modelType: typeString });

        //const routeParams: RawParams = {
        //    page: `${typeString}s`,
        //    model: modelId,
        //    modelType: typeString
        //};

        //this.goToState(PageStateNames.COMPONENTS_RESOURCEMANAGEMENT, routeParams);
    }

    goToTMSPage(vehicle: IVehicle) {
        const routeParams: RawParams = { page: '', argument1: vehicle.ShortReg };
        this.goToState(TmsModuleNavigation.ActiveRoutes, routeParams);
    }

    goToPlanningPage(vehicle: IVehicle) {
        const routeParams: RawParams = { page: '', argument1: vehicle.ShortReg };
        this.goToState(FleetNavigationModule.Planning, routeParams);
    }

    goToPlanning() {
        const routeParams: RawParams = { page: '' };
        this.goToState(FleetNavigationModule.Planning, routeParams);
    }

    goToUnauthorizedPage() {
        const routeParams: RawParams = { page: '' };
        this.goToState(PageStateNames.AUTHORIZATION_UNAUTHORIZED, routeParams);
    }

    getState(): StateService {
        return this.$state;
    }

    getDefaultRouteForUser(accountData?: IAccount): string {
        const userIsFoundationUser = this.$rootScope.currentUser?.platform == UserPlatform.Foundation;

        // foundation user
        if (userIsFoundationUser) {

            if (this.$rootScope.currentUser?.Type == UserType.InternalUser && this.isRouteAllowedForMobileUser(FoundationNavigationModule.Default)) {
                return FoundationNavigationModule.Default;
            }

            if (this.$rootScope.currentUser?.Type == UserType.Engineer && this.isRouteAllowedForMobileUser(FoundationNavigationModule.DefaultEngineer)) {
                return FoundationNavigationModule.DefaultEngineer;
            }

        }

        // no account data
        if (!accountData) {
            return PageStateNames.AUTHORIZATION_UNAUTHORIZED;
        }

        // fleet user
        const defaultFleetRoute = this.getDefaultFleetRouteForAccountAndCurrentUser(accountData);

        if (defaultFleetRoute && this.isRouteAllowedForMobileUser(defaultFleetRoute)) {
            return defaultFleetRoute;
        }

        // camera user
        if (this.accountFeatureService.accountAndUserHaveAllFeatureAccess(accountData, this.$rootScope.currentUser, [AccountFeatures.Camera])
            && this.isRouteAllowedForMobileUser(CameraModule.Default)) {
            return CameraModule.Default;
        }

        // geofences user
        if (this.accountFeatureService.doesCurrentUserHaveSpecialPermission(SpecialUserPermissions.ManageGeofences)
            && this.isRouteAllowedForMobileUser(FleetNavigationModule.Geofences)) {
            return FleetNavigationModule.Geofences;
        }

        // tms user
        if (this.accountFeatureService.accountAndUserHaveAllFeatureAccess(accountData, this.$rootScope.currentUser, [AccountFeatures.TMS])
            && this.isRouteAllowedForMobileUser(TmsModuleNavigation.Default)) {
            return TmsModuleNavigation.Default;
        }

        // reports user
        if (this.accountFeatureService.doesCurrentUserHaveSpecialPermission(SpecialUserPermissions.ShowReports)
            && this.isRouteAllowedForMobileUser(ReportsNavigationModule.Default)) {
            return ReportsNavigationModule.Default;
        }

        // resource management user
        const defaultResourceManagementRoute = this.getDefaultResourceManagementRouteForCurrentUser();

        if (defaultResourceManagementRoute && this.isRouteAllowedForMobileUser(defaultResourceManagementRoute)) {
            return defaultResourceManagementRoute;
        }

        // temperatures user
        if (this.accountFeatureService.currentUserHasFeatureAccess(AccountFeatures.Temperature)) {
            return TemperatureNavigationModule.Default;
        }

        // alert review user
        if (this.accountFeatureService.currentUserHasRole([UserRole.ManageAlerts])) {
            return AlertsNavigationModule.Default;
        }
        else if (this.accountFeatureService.currentUserHasRole([UserRole.ManageAlertReviews])) {
            return AlertsNavigationModule.ReviewAlerts;
        }

        // unauthorized
        return PageStateNames.AUTHORIZATION_UNAUTHORIZED;
    }

    // -------------------

    private isRouteAllowedForMobileUser(route: string): boolean {
        const userIsMobile = this.constantsService.getIsMobile();

        if (userIsMobile && !this.mobileOnlyFeatureRoutes.includes(route)) {
            return false;
        }

        return true;
    }

    private getDefaultFleetRouteForAccountAndCurrentUser(accountData: IAccount): string {
        const fleetFeatures: AccountFeatures[] = [
            AccountFeatures.TrackingDevice,
            AccountFeatures.Messaging,
            AccountFeatures.Tacho,
            AccountFeatures.Temperature,
            AccountFeatures.Breathalock
        ];

        let defaultMappedRoute: string;

        for (let feature of fleetFeatures) {
            const matchingDefaultMappedRoute = this.defaultFeatureRouteMap[feature];

            if (matchingDefaultMappedRoute && this.accountFeatureService.accountAndUserHaveFeatureAccess(accountData, this.$rootScope.currentUser, feature)) {
                defaultMappedRoute = matchingDefaultMappedRoute;

                break;
            }
        }

        if (defaultMappedRoute && this.accountFeatureService.doesCurrentUserHaveSpecialPermission(SpecialUserPermissions.ShowHistoricalPlots)) {
            return FleetNavigationModule.Default;
        }

        return defaultMappedRoute;
    }

    private getDefaultResourceManagementRouteForCurrentUser(): string {
        if (this.accountFeatureService.currentUserHasRole([UserRole.Administrator])) {
            return ResourceManagementNavigationModule.Default;
        }

        const resourceManagementRoles: string[] = Object.keys(this.defaultResourceManagementRouteMap);

        let defaultMappedRoute: string;

        for (let role of resourceManagementRoles) {
            const matchingDefaultMappedRoute = this.defaultResourceManagementRouteMap[role];

            if (matchingDefaultMappedRoute && this.accountFeatureService.currentUserHasRole([role])) {
                defaultMappedRoute = matchingDefaultMappedRoute;

                break;
            }
        }

        return defaultMappedRoute;
    }

    private goToState(to: StateOrName, routeParams: RawParams, reloadIfSame = true) {
        const reload = reloadIfSame && (this.$state.current.name.indexOf(to.toString()) == 0);

        this.$state.go(to, routeParams, { location: true, notify: false, reload: reload });
    }

    private canUserAccessRoute(route: string): boolean {
        if (this.$rootScope.currentUser && route == PageStateNames.AUTHENTICATION_LOGIN) {
            return false;
        }

        const userIsFoundationUser = this.$rootScope.currentUser?.platform == UserPlatform.Foundation;
        const newStateIsFoundation = route.indexOf(FoundationNavigationModule.ParentRoot) == 0;

        return userIsFoundationUser == newStateIsFoundation;
    }
}
